import React from 'react';
import useMapFromArray, { Map } from 'hooks/use-map-from-array';
import { useNodeLinking } from 'state/canvas/node-linking';
import { useSideDrawerOperations } from 'state/side-drawer';
import {
  useGlobalLinkOperations,
  useGlobalLinks,
} from 'state/canvas/node-linking/global-links';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import { useTranslation } from 'i18n';
import { useParams } from 'react-router';
import surfaceNodeQuery from 'graphql/surface-node/queries/surface-node';
import toast from 'services/toast';
import { isEmpty, set } from 'lodash/fp';
import { SnippetOperation } from 'graphql/types';
import { generateSequentialUUID } from 'utils/js';
import { AnnotationData, AnyObject } from 'types';

interface Snippet extends AnyObject {
  id: string;
  annotationId: string;
  text?: string;
  name?: string;
  isNamed?: boolean;
  isLink?: boolean;
  operation?: SnippetOperation;
}

interface UseSurfaceNodeTextSnippets {
  snippets: Map<string, Snippet>;
  saveSnippet(data: AnnotationData, defaultName?: string): void;
  deleteSnippetByAnnotationId(annotationId: string): void;
  onTextSnippetSave(data: AnnotationData): void;
  onTextSnippetDelete(data: AnnotationData): void;
  onDeleteLink(data: AnnotationData): void;
  onStartLinking(form: any): (data: AnnotationData) => void;
  onFinishLinking(form: any): (data: AnnotationData) => void;
  onGlobalLinkIndicatorClick(data: AnnotationData): void;
  shouldDeleteSnippet(
    data: AnnotationData,
    snippet: Snippet,
    globalLink: any,
  ): boolean;
  shouldDeleteNamedSnippet(data: AnnotationData, snippet: Snippet): boolean;
  shouldDeleteLinkSnippet(
    data: AnnotationData,
    snippet: Snippet,
    globalLink: any,
  ): boolean;
  removeDeletedSnippetFromGlobalLinkZone(snippets: Snippet[]): void;
  shouldShowDeleteLinkAction(data: AnnotationData): boolean;
  isSnippetInGlobalLinkZone(snippet: Snippet): boolean;
  onCopy(data: AnnotationData[]): void;
  onCut(data: AnnotationData[]): void;
  onPaste(data: AnnotationData): void;
  showFinishLinkAction: boolean;
}

interface UseSurfaceNodeTextSnippetsOperations {
  removeDeletedSnippetFromGlobalLinkZone(snippets: Snippet[]): void;
}

export default function useSurfaceNodeTextSnippets(
  formSnippets: Snippet[] | undefined,
  onSubmit: (values: AnyObject, actions: any, options?: any) => Promise<any>,
  closeForm: Function,
): UseSurfaceNodeTextSnippets {
  const { t } = useTranslation();
  const { id } = useParams();

  const snippets = useMapFromArray<string, any>(formSnippets, 'annotationId');

  const copiedSnippets = useMapFromArray<string, any>([], 'annotationId');
  const cutSnippets = useMapFromArray<string, any>([], 'annotationId');

  const saveNodeInCache = useSaveNodeInCache();

  const {
    removeDeletedSnippetFromGlobalLinkZone,
  } = useSurfaceNodeTextSnippetsOperations();

  const [_, { createLink, linkNodes }] = useNodeLinking(id as string);

  const { setNodeToDrawer } = useSideDrawerOperations();

  const formValues = React.useRef<AnyObject>({});

  const [
    [globalLink],
    { addGlobalLink, removeGlobalLinkByNodeId, clearGlobalLinks },
  ] = useGlobalLinks();

  function onTextSnippetSave(data: AnnotationData) {
    const existingSnippet = snippets.get(data.annotationId);

    const snippet = {
      ...existingSnippet,
      name: data.name,
      text: data.text,
      annotationId: data.annotationId,
      operation: snippets.has(data.annotationId)
        ? SnippetOperation.Update
        : SnippetOperation.Create,
    };

    snippets.set(data.annotationId, snippet);

    formValues.current = set(
      'snippets',
      Array.from(snippets.values()),
      formValues.current,
    );
  }

  function onTextSnippetDelete(data: AnnotationData) {
    deleteSnippetByAnnotationId(data.annotationId);
  }

  function onStartLinking(form) {
    formValues.current = form.values;

    return async function (data: AnnotationData) {
      form.setSubmitting(true);

      function addSnippetToGlobalLinkZone(snippet) {
        if (snippet) {
          const from = createLink(snippet);
          addGlobalLink({ from, to: null });
          toast.success(
            t(
              'Node was successfully auto saved and text snippet was added to global link zone.',
            ),
          );
        }
      }

      const defaultName = form.values.name + ' text snippet';

      saveSnippet(data, defaultName);

      return new Promise((resolve) => {
        setTimeout(async () => {
          const latestFormData = set(
            'snippets',
            Array.from(snippets.values()),
            formValues.current,
          );

          formValues.current = latestFormData;

          form.setSubmitting(true);

          return onSubmit(latestFormData, form).then((response) => {
            form.setSubmitting(false);

            if (response.isSuccess) {
              const surfaceNode = response.getField('data');

              form.setValues(surfaceNode);

              const snippetNode = surfaceNode.snippets.find(
                ({ annotationId }) => annotationId === data.annotationId,
              );

              addSnippetToGlobalLinkZone(snippetNode);
            }

            return resolve(response);
          });
        }, 500);
      });
    };
  }

  function onDeleteLink(data: AnnotationData) {
    const snippetToDelete = snippets.get(data.annotationId);

    if (snippetToDelete) {
      removeGlobalLinkByNodeId(snippetToDelete.id);

      if (data.name) {
        saveSnippet(data);
      } else {
        deleteSnippetByAnnotationId(data.annotationId);
      }
    }
  }

  function onFinishLinking(form) {
    formValues.current = form.values;
    // additionalFromData is used to pass latest PSPDF kit instantJSON or slateData to the form before save
    return async function (data: AnnotationData) {
      // Check if snippet exists if not create it
      const defaultName = form.values.name + ' text snippet';

      saveSnippet(data, defaultName);

      return new Promise((resolve) => {
        setTimeout(async () => {
          const latestFormData = set(
            'snippets',
            Array.from(snippets.values()),
            formValues.current,
          );

          formValues.current = latestFormData;

          form.setSubmitting(true);

          const response = await onSubmit(latestFormData, form, {
            closeTheForm: false,
          });

          form.setSubmitting(false);

          if (response.isSuccess) {
            clearGlobalLinks();

            const surfaceNode = response.getField('data');

            form.setValues(surfaceNode);

            const snippetNode = surfaceNode.snippets.find(
              ({ annotationId }) => annotationId === data.annotationId,
            );

            if (snippetNode) {
              const { from } = globalLink;

              const to = createLink(snippetNode);

              const linkResponse = await linkNodes({ from, to });

              if (linkResponse.isSuccess) {
                const [toNodeResponse, fromNodeResponse] = await Promise.all([
                  surfaceNodeQuery({
                    id: to.id,
                  }),
                  surfaceNodeQuery({
                    id: from.id,
                  }),
                ]);

                // Update snippet with latest value.
                snippets.set(
                  snippetNode.annotationId,
                  toNodeResponse.getField('data'),
                );

                // If from node is also from the same document we need to update from snippet
                //@ts-ignore
                if (from.containerNode.id === surfaceNode.id) {
                  const fromSnippetNode = surfaceNode.snippets.find(
                    ({ id }) => id === from.id,
                  );

                  if (fromSnippetNode) {
                    snippets.set(
                      fromSnippetNode.annotationId,
                      fromNodeResponse.getField('data'),
                    );

                    saveNodeInCache(fromNodeResponse.getField('data'));
                  }
                }

                saveNodeInCache(toNodeResponse.getField('data'));

                toast.success(t('Successfully linked text snippet to node.'));
              } else {
                toast.error(response.getErrorMessage());
              }
            }
          }

          resolve(response);
        }, 500);
      });
    };
  }

  function onGlobalLinkIndicatorClick(data: AnnotationData) {
    const snippetNode = snippets.get(data.annotationId);

    if (snippetNode) {
      // hide the modal
      closeForm();
      setNodeToDrawer(snippetNode);
    }
  }

  function saveSnippet(data: AnnotationData, defaultName?: string) {
    console.log('------------- SAVE SNIPPET -------------------------');
    if (snippets.has(data.annotationId)) {
      const snippet = snippets.get(data.annotationId);

      const operation = snippet.id
        ? SnippetOperation.Update
        : SnippetOperation.Create;

      snippets.set(data.annotationId, {
        ...snippet,
        ...data,
        operation,
      });
    } else {
      const newSnippet = {
        name: data.name ?? defaultName,
        text: data.text,
        annotationId: data.annotationId,
        operation: SnippetOperation.Create,
      };

      snippets.set(data.annotationId, newSnippet);
    }

    formValues.current = set(
      'snippets',
      Array.from(snippets.values()),
      formValues.current,
    );
  }

  function deleteSnippetByAnnotationId(annotationId: string) {
    const snippetToDelete = snippets.get(annotationId);

    if (snippetToDelete?.id) {
      snippets.set(
        annotationId,
        set('operation', SnippetOperation.Delete, snippetToDelete),
      );
    } else {
      snippets.delete(annotationId);
    }

    formValues.current = set(
      'snippets',
      Array.from(snippets.values()),
      formValues.current,
    );
  }

  function shouldDeleteSnippet(
    { isLink, isNamed }: AnnotationData,
    snippet: Snippet,
    globalLink,
  ): boolean {
    const isSavedSnippetWithoutLinks =
      snippet?.id && isEmpty(snippet?.pointsTo);
    const isLinkWithoutName = isLink && !isNamed;
    const notInGlobalLinkZone = snippet?.id !== globalLink?.from?.id;
    const snippetForAnnotationDoesNotExist = !snippet && (isLink || isNamed);

    return Boolean(
      snippetForAnnotationDoesNotExist ||
        (isSavedSnippetWithoutLinks &&
          isLinkWithoutName &&
          notInGlobalLinkZone),
    );
  }

  function shouldDeleteLinkSnippet(
    { isLink }: AnnotationData,
    snippet: Snippet,
    globalLink,
  ): boolean {
    const isSavedSnippetWithoutLinks =
      snippet?.id && isEmpty(snippet?.pointsTo);

    const notInGlobalLinkZone = snippet?.id !== globalLink?.from?.id;

    return Boolean(isLink && isSavedSnippetWithoutLinks && notInGlobalLinkZone);
  }

  function shouldDeleteNamedSnippet(
    { isNamed }: AnnotationData,
    snippet: Snippet,
  ) {
    return !snippet && isNamed;
  }

  function isSnippetInGlobalLinkZone(snippet) {
    return globalLink?.from && snippet && globalLink?.from?.id === snippet?.id;
  }

  function shouldShowDeleteLinkAction(data: AnnotationData): boolean {
    const { name, isLink, annotationId } = data;

    const snippet = snippets.get(annotationId);

    const snippetIsInLinkZone =
      globalLink && snippet && globalLink?.from?.id === snippet?.id;

    const onlyLinkedSnippet = !name && isLink;

    return Boolean(snippetIsInLinkZone && onlyLinkedSnippet);
  }

  function onCopy(data: AnnotationData[]): void {
    if (copiedSnippets.size) {
      copiedSnippets.clear();
    }

    data.forEach((data) => {
      snippets.delete(data.annotationId);
      const annotationId = generateSequentialUUID();

      copiedSnippets.set(annotationId, {
        ...data,
        annotationId,
        operation: SnippetOperation.Create,
      });
    });
  }

  function onCut(data: AnnotationData[]): void {
    cutSnippets.clear();
    copiedSnippets.clear();

    data.forEach((data) => {
      deleteSnippetByAnnotationId(data.annotationId);
      cutSnippets.set(data.annotationId, data);
    });
  }

  async function onPaste() {
    if (cutSnippets.size) {
      cutSnippets.forEach((data) => snippets.set(data.annotationId, data));
    }

    if (copiedSnippets.size) {
      copiedSnippets.forEach((data) => snippets.set(data.annotationId, data));
    }

    // Add from copied nodes
    // Clear copied nodes
    copiedSnippets.clear();
    cutSnippets.clear();
  }

  return {
    snippets,
    saveSnippet,
    deleteSnippetByAnnotationId,
    onTextSnippetSave,
    onTextSnippetDelete,
    onDeleteLink,
    onFinishLinking,
    onGlobalLinkIndicatorClick,
    onStartLinking,
    shouldDeleteSnippet,
    shouldDeleteLinkSnippet,
    shouldDeleteNamedSnippet,
    removeDeletedSnippetFromGlobalLinkZone,
    shouldShowDeleteLinkAction,
    isSnippetInGlobalLinkZone,
    showFinishLinkAction: Boolean(globalLink),
    onCopy,
    onCut,
    onPaste,
  };
}

export function useSurfaceNodeTextSnippetsOperations(): UseSurfaceNodeTextSnippetsOperations {
  const { removeGlobalLinkByNodeId } = useGlobalLinkOperations();

  function removeDeletedSnippetFromGlobalLinkZone(snippets: Snippet[]) {
    snippets
      ?.filter(({ operation }) => operation === SnippetOperation.Delete)
      // Remove global link checks if there's link in global zone before removing
      ?.map((snippetToDelete) => removeGlobalLinkByNodeId(snippetToDelete.id));
  }

  return { removeDeletedSnippetFromGlobalLinkZone };
}
